import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  TableContainer,
  TextField,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TopPage from 'components/Shared/TopPage';
import { getAccessGroups, AccessGroup } from 'services/user';
import api from 'services/api';
import DataTable from 'components/Shared/Table';
import Loader from 'components/Shared/Loader';
import type { MUIDataTableState } from 'mui-datatables';
import { useNavigate } from 'react-router-dom';
import { primary } from 'helpers/colors';
import { useSnackbar } from 'notistack';

import { CustomCardContent } from './style';

const renderActive = (value: boolean) => (value ? <CheckCircleIcon color='success' /> : <CancelIcon color='error' />);

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

function AccessGroups() {
  const { enqueueSnackbar } = useSnackbar();
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestId = useRef(0);

  const [groupToDelete, setGroupToDelete] = useState<AccessGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  const loadGroups = useCallback(() => {
    setLoading(true);
    requestId.current += 1;
    const currentId = requestId.current;
    getAccessGroups(
      (data: AccessGroup[], total: number) => {
        if (currentId !== requestId.current) return;
        setGroups(data);
        setTotalCount(total);
        setLoading(false);
      },
      {
        perPage: rowsPerPage,
        page: page,
        search: debouncedSearch,
        order: sortField ?? undefined,
        direction: sortField ? sortDirection : undefined
      }
    );
  }, [page, rowsPerPage, debouncedSearch, sortField, sortDirection]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreate = () => {
    navigate('/grupos-acesso/novo');
  };

  const confirmDeleteGroup = useCallback(async () => {
    if (!groupToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/access_groups/${groupToDelete.id}`);
      enqueueSnackbar('Grupo de acesso removido com sucesso', { variant: 'success' });
      setGroupToDelete(null);
      loadGroups();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setGroupToDelete(null);
        enqueueSnackbar('Este grupo possui usuários vinculados. Desvincule-os antes de excluí-lo.', {
          variant: 'warning'
        });
      } else {
        enqueueSnackbar('Erro ao remover grupo de acesso', { variant: 'error' });
      }
    } finally {
      setIsDeleting(false);
    }
  }, [groupToDelete, enqueueSnackbar, loadGroups]);

  const formattedData = useMemo(() => [...groups], [groups]);

  const renderActions = useCallback(
    (_value: any, tableMeta: any) => {
      const group = groups[tableMeta.rowIndex];
      if (!group) return null;
      return (
        <Box display='flex' gap={0.5}>
          <Tooltip title='Visualizar'>
            <IconButton
              size='small'
              color='primary'
              onClick={e => {
                e.stopPropagation();
                navigate(`/grupos-acesso/${group.id}/gerenciar`);
              }}
            >
              <OpenInNewIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Remover'>
            <IconButton
              size='small'
              color='error'
              onClick={e => {
                e.stopPropagation();
                setGroupToDelete(group);
              }}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    [groups, navigate]
  );

  const renderSortableLabel = useCallback(
    (label: string, field: string) => {
      const isActive = sortField === field;
      let SortIcon = UnfoldMoreIcon;
      if (isActive && sortDirection === 'asc') SortIcon = ArrowUpwardIcon;
      if (isActive && sortDirection === 'desc') SortIcon = ArrowDownwardIcon;
      return (
        <Box display='flex' alignItems='center' gap={0.5} sx={{ userSelect: 'none' }}>
          {label}
          <SortIcon sx={{ fontSize: 14, opacity: isActive ? 0.7 : 0.4 }} />
        </Box>
      );
    },
    [sortField, sortDirection]
  );

  const columns = useMemo(
    () => [
      {
        name: 'active',
        label: 'Ativo',
        options: {
          sort: false,
          customBodyRender: renderActive,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'name',
        label: 'Identificador',
        options: {
          customHeadLabelRender: () => renderSortableLabel('Identificador', 'name'),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'display_name',
        label: 'Nome',
        options: {
          customHeadLabelRender: () => renderSortableLabel('Nome', 'display_name'),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'cnpjs_count',
        label: 'CNPJs',
        options: {
          customBodyRender: (value: number) => value ?? 0,
          customHeadLabelRender: () => renderSortableLabel('CNPJs', 'cnpjs_count'),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'users_count',
        label: 'Usuários',
        options: {
          customBodyRender: (value: number) => value ?? 0,
          customHeadLabelRender: () => renderSortableLabel('Usuários', 'users_count'),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'created_at',
        label: 'Data de Criação',
        options: {
          customBodyRender: (value: string) => formatDate(value),
          customHeadLabelRender: () => renderSortableLabel('Data de Criação', 'created_at'),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'id',
        label: 'Ações',
        options: {
          sort: false,
          customBodyRender: renderActions,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      }
    ],
    [renderActions, renderSortableLabel]
  );

  const onChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const onSearch = (search: string) => {
    setSearchText(search);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
  };

  const onSortTable = useCallback((field: string | null, direction: 'asc' | 'desc') => {
    setSortField(field);
    if (direction) setSortDirection(direction);
    setPage(1);
  }, []);

  const handleRowClick = useCallback(
    (rowData: any, rowMeta: any) => {
      const idx = rowMeta?.dataIndex;
      if (typeof idx === 'number') {
        const ag = formattedData[idx];
        if (ag && ag.id) {
          navigate(`/grupos-acesso/${ag.id}/gerenciar`);
        }
      }
    },
    [formattedData, navigate]
  );

  const handleTableChange = useCallback(
    (action: string, tableState: MUIDataTableState) => {
      switch (action) {
        case 'changePage':
          return onChangePage(tableState.page + 1);
        case 'changeRowsPerPage':
          return onChangeRowsPerPage(tableState.rowsPerPage);
        case 'search':
          return onSearch(tableState.searchText || '');
        case 'sort': {
          const { name, direction } = tableState.sortOrder;
          return onSortTable(name || null, direction);
        }
        default:
          return console.info(`action (${action}) not handled.`);
      }
    },
    [onSortTable]
  );

  const optionsObj = useMemo(
    () => ({
      page: page - 1,
      count: totalCount,
      rowsPerPage,
      jumpToPage: true,
      onTableChange: handleTableChange,
      onRowClick: handleRowClick,
      serverSide: true,
      search: false,
      searchAlwaysOpen: false,
      searchText,
      selectableRows: 'none',
      sort: true,
      sortThirdClickReset: true,
      sortOrder: sortField ? { name: sortField, direction: sortDirection } : {},
      setRowProps: () => ({ style: { cursor: 'pointer' } }),
      textLabels: {
        body: {
          noMatch: 'Nenhum registro encontrado',
          toolTip: 'Ordenar'
        }
      }
    }),
    [page, totalCount, rowsPerPage, handleTableChange, handleRowClick, searchText, sortField, sortDirection]
  );

  return (
    <>
      <TopPage title='Grupos de Acesso' />
      <Card style={CustomCardContent}>
        <Box display='flex' gap={2} alignItems='center' mb={1} mx={3} mt={3}>
          <TextField
            value={searchText}
            onChange={({ target: { value } }) => {
              setSearchText(value);
              if (debounceTimer.current) clearTimeout(debounceTimer.current);
              debounceTimer.current = setTimeout(() => {
                setDebouncedSearch(value);
                setPage(1);
              }, 500);
            }}
            placeholder='Pesquisar por Identificador ou Nome'
            variant='outlined'
            size='small'
            sx={{ input: { fontSize: '0.9rem' }, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
          />
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disableElevation
            sx={{
              borderRadius: 10,
              whiteSpace: 'nowrap',
              py: 0.7,
              '&:hover': { backgroundColor: primary.hover }
            }}
          >
            Novo Grupo de Acesso
          </Button>
        </Box>

        <CardContent>
          <TableContainer
            component={Paper}
            sx={{
              '& tbody tr:hover': { cursor: 'pointer', backgroundColor: 'action.hover' },
              '& .MuiTableSortLabel-icon': { display: 'none' }
            }}
            elevation={0}
          >
            {isLoading ? <Loader /> : <DataTable columns={columns} data={formattedData} optionsObj={optionsObj} />}
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={groupToDelete !== null} onClose={() => !isDeleting && setGroupToDelete(null)}>
        <DialogTitle>Remover grupo de acesso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover o grupo <strong>{groupToDelete?.display_name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ paddingTop: 5, marginBottom: 10, marginRight: 12 }}>
          <Button onClick={() => setGroupToDelete(null)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={confirmDeleteGroup}
            disabled={isDeleting}
            disableElevation
            startIcon={isDeleting ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap' }}
          >
            {isDeleting ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AccessGroups;
